import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { AuthModal } from "./AuthModal";

const AuthGateContext = createContext(null);

// Read the session from storage at call time. Components receive isLoggedIn as
// a prop computed during App's render, which is stale immediately after the
// modal signs someone in - relying on it there causes the resumed action to
// re-enter the auth gate.
export const hasSession = () => !!localStorage.getItem("accesstoken");

/**
 * Wrap the app once, then call requireAuth(fn) from anywhere.
 *
 *   const { requireAuth, isLoggedIn } = useAuthGate();
 *   <button onClick={() => requireAuth(() => navigate('/checkout'), 'Verify your number to check out.')}>
 *     Buy now
 *   </button>
 *
 * If already signed in, fn runs immediately. If not, the sheet opens and fn
 * runs on success — the caller doesn't care which happened, and the page
 * never unmounts, so nothing is lost.
 */
export const AuthGateProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [allMethods, setAllMethods] = useState(false);
  const [context, setContext] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => hasSession()
  );
  const pendingRef = useRef(null);

  // options.allMethods: offer every sign-in, not just the store's (AuthModal).
  // options.context: what the sign-in is for (buy, gift…), for analytics only.
  const requireAuth = useCallback((action, why, options) => {
    if (hasSession()) {
      action();
      return;
    }
    pendingRef.current = action;
    setReason(why || "");
    setAllMethods(!!options?.allMethods);
    setContext(options?.context || "other");
    setOpen(true);
  }, []);

  const handleSuccess = useCallback(() => {
    setOpen(false);
    setIsLoggedIn(true);
    const action = pendingRef.current;
    pendingRef.current = null;
    // Let the sheet finish closing before the resumed action renders.
    if (action) setTimeout(action, 0);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    pendingRef.current = null;
  }, []);

  return (
    <AuthGateContext.Provider value={{ requireAuth, isLoggedIn, setIsLoggedIn }}>
      {children}
      <AuthModal
        open={open}
        reason={reason}
        allMethods={allMethods}
        context={context}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    </AuthGateContext.Provider>
  );
};

export const useAuthGate = () => {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error("useAuthGate must be used inside <AuthGateProvider>");
  return ctx;
};
